<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api, type GoldVolRegimeResponse, type GoldSynthNavResponse, type GoldXauResponse, type GoldTreasuryYieldsResponse, type GoldKtLatestResponse, type GoldFuturesSnapshotResponse } from '../api/client'
import { deriveGoldOverlay } from '../utils/goldOverlay'
import { sparkSegments } from './sparkline'

// Mirror of SparklineBar.vue's pattern, scoped to the gold portfolio.
// Compact clickable row at the top of the gold signal screen — same
// visual language as BGA (label / sparkline / current value). Clicking
// any item navigates to the matching full-chart route under /charts/.

const vol = ref<GoldVolRegimeResponse | null>(null)
const synth = ref<GoldSynthNavResponse | null>(null)
const xau = ref<GoldXauResponse | null>(null)
const tsy = ref<GoldTreasuryYieldsResponse | null>(null)
const gc = ref<GoldFuturesSnapshotResponse | null>(null)
const loading = ref(true)

onMounted(async () => {
  try {
    const [v, s, x, t, k, g] = await Promise.all([
      api.goldVolRegime().catch(() => null),
      api.goldSynthNav().catch(() => null),
      api.goldXau().catch(() => null),
      api.goldTreasuryYields().catch(() => null),
      api.goldKtLatest().catch(() => null),
      // V108 GOLD-vs-GC 24h series — powers the new spark-gc item + click
      // through to /charts/goldvsgc. Cold-start empty until the first
      // 15-min bucket flushes; UI degrades to "—" in that case.
      api.chartGoldFuturesSnapshots('24h').catch(() => null),
    ])
    vol.value = v
    synth.value = s
    xau.value = x
    ktLatest.value = k
    tsy.value = t
    gc.value = g
  } finally {
    loading.value = false
  }
})

function goChart(path: string) {
  if (window.location.pathname === path) return
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

// Segmented sparkline — same Bloomberg-style per-segment colouring as
// SparklineBar. `upIsGood=false` for GVZ (rising vol = rising risk =
// red); `upIsGood=true` for K_t (rising synth NAV = good).
// sparkSegments lives in ./sparkline (2026-11-02) — one implementation for every bar.

const gvzSpark = computed(() => {
  const pts = vol.value?.sparkline?.map(p => p.c) ?? []
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ false), values: pts }
})

const ktSpark = computed(() => {
  // K_t series: prefer the synthesised series points where K is non-null.
  const pts = synth.value?.series
    ?.filter(p => p.k != null)
    .map(p => Number(p.k)) ?? []
  // Cap to last 60 points so the spark stays "recent" (~3 months) rather
  // than 2 years of dense data which compresses each step illegibly.
  const recent = pts.slice(-60)
  return { segments: sparkSegments(recent, 80, 18, /* upIsGood */ true), values: recent }
})

// XAUUSD — up=green (the fund is long gold). Value column = 1d % move,
// mirroring the NAS item on the BGA bar (the template for this item).
const xauSpark = computed(() => {
  const pts = xau.value?.sparkline?.map(p => p.c) ?? []
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ true), values: pts }
})

// COMEX GC — up=green (the fund is long gold; gold futures rising is
// consonant with the underlying). Value column = 24h % move. Series comes
// from V108's /api/charts/gold-futures-snapshots — the same series the
// full GOLD-vs-GC chart uses, so click-through renders the same shape at
// a larger scale.
const gcSpark = computed(() => {
  const pts = gc.value?.candles?.map(c => c.gc.close) ?? []
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ true), values: pts }
})
const gcRet1d = computed(() => {
  const pts = gcSpark.value.values
  if (pts.length < 2 || pts[0] === 0) return null
  return (pts[pts.length - 1] / pts[0] - 1) * 100
})

// 10y Treasury yield — up=red (rising yields raise the opportunity cost of
// holding zero-carry gold). Value column = latest yield level in %.
const y10Spark = computed(() => {
  const pts = tsy.value?.sparkline?.map(p => p.c) ?? []
  return { segments: sparkSegments(pts, 80, 18, /* upIsGood */ false), values: pts }
})

const gvzValue = computed(() => vol.value?.latestClose?.toFixed(2) ?? '—')
// K_t figure (2026-08-16 fix, paolo): show the K_t synth NAV's day RETURN
// (kt-latest returnFrac — same authority as the signal panel's figure), not
// the K_t-vs-published deviation, which read as a tiny tracking error
// (+0.26%) while the fund was down -2.87%.
const ktLatest = ref<GoldKtLatestResponse | null>(null)
// Gold K_t live overlay bridge (2026-09-02): when overlay-eligible fields
// are present on kt-latest, the day-return shown here switches to
// combinedReturnFrac (base K_t + β·Δgold since the 16:00 NY anchor) so this
// bar agrees with GoldSignalPanel's overlay line and the P&L bridge, rather
// than showing the stale frozen base return during the overlay window.
const ktOverlay = computed(() => deriveGoldOverlay(ktLatest.value))
const ktValue = computed(() => {
  if (ktOverlay.value.active && ktOverlay.value.combinedFrac != null) {
    return ktOverlay.value.combinedFrac * 100
  }
  const rf = ktLatest.value?.available ? ktLatest.value?.returnFrac : null
  if (rf != null) return rf * 100
  // Fallback: last-two-point return of the synthesised K series.
  const pts = synth.value?.series?.filter(p => p.k != null).map(p => Number(p.k)) ?? []
  if (pts.length < 2 || pts[pts.length - 2] === 0) return null
  return (pts[pts.length - 1] / pts[pts.length - 2] - 1) * 100
})

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—'
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(2)}%`
}

function returnClass(v: number | null | undefined): string {
  if (v == null) return ''
  return v >= 0 ? 'positive' : 'negative'
}

const xauRet1d = computed(() => xau.value?.ret1dPct ?? null)

const y10Value = computed(() => {
  const v = tsy.value?.latest10y
  return v != null ? v.toFixed(2) + '%' : '—'
})
// Colour the 10y level by the direction of the 1d move: rising = red
// (anti-gold), falling = green. Neutral when no data.
const y10Class = computed(() => {
  const c = tsy.value?.chg1dBps10y
  if (c == null) return ''
  return c > 0 ? 'negative' : 'positive'
})

// GVZ regime colour for the value column. Same scale as full panel:
// low=green, normal=neutral, elevated=amber, extreme=red.
const gvzColourClass = computed(() => {
  switch (vol.value?.regime) {
    case 'low':      return 'positive'
    case 'normal':   return ''
    case 'elevated': return 'amber'
    case 'extreme':  return 'negative'
    default:         return ''
  }
})
</script>

<template>
  <div v-if="!loading" class="sparkline-bar">
    <!-- XAUUSD — up=green (long gold) -->
    <div class="spark-item" data-testid="spark-xau" @click="goChart('/charts/gold-xau')">
      <span class="spark-label xau">XAU</span>
      <svg v-if="xauSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in xauSpark.segments" :key="`x${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(xauRet1d)">{{ fmtPct(xauRet1d) }}</span>
    </div>

    <!-- 10y Treasury yield — up=red (rising yields anti-gold) -->
    <div class="spark-item" data-testid="spark-10y" @click="goChart('/charts/gold-10y')">
      <span class="spark-label y10">10Y</span>
      <svg v-if="y10Spark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in y10Spark.segments" :key="`t${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="y10Class">{{ y10Value }}</span>
    </div>

    <!-- GVZ — up=red (rising vol bad) -->
    <div class="spark-item" @click="goChart('/charts/gold-gvz')">
      <span class="spark-label gvz">GVZ</span>
      <svg v-if="gvzSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in gvzSpark.segments" :key="`g${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="gvzColourClass">{{ gvzValue }}</span>
    </div>

    <!-- GC (COMEX gold futures) — up=green (fund is long gold). Value = 24h % move.
         Full chart at /charts/goldvsgc plots gold NAV vs GC bars in the same
         style as JH-vs-NAS and BGA-vs-NAS. -->
    <div class="spark-item" data-testid="spark-gc" @click="goChart('/charts/goldvsgc')">
      <span class="spark-label gc">GC</span>
      <svg v-if="gcSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in gcSpark.segments" :key="`c${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(gcRet1d)">{{ fmtPct(gcRet1d) }}</span>
    </div>

    <!-- K_t (synth NAV) — up=green; value shows the K_t day return (kt-latest) -->
    <div class="spark-item" @click="goChart('/charts/gold-kt')">
      <span class="spark-label kt">K_t</span>
      <svg v-if="ktSpark.segments.length" viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-for="(s, i) in ktSpark.segments" :key="`k${i}`"
              :x1="s.x1" :y1="s.y1" :x2="s.x2" :y2="s.y2"
              :stroke="s.stroke" stroke-width="1.5" />
      </svg>
      <svg v-else viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line x1="0" y1="9" x2="80" y2="9" stroke="#1c2128" stroke-width="1" stroke-dasharray="4,3" />
      </svg>
      <span class="spark-value" :class="returnClass(ktValue)">{{ fmtPct(ktValue) }}</span>
      <span v-if="ktOverlay.active && ktOverlay.combinedFrac != null" class="overlay-marker"
            data-testid="gold-sparkline-overlay-marker" title="live gold overlay applied">*</span>
    </div>
  </div>
</template>

<style scoped>
.sparkline-bar {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  gap: 14px;
  padding: 6px 12px;
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
}
.spark-item { display: flex; align-items: center; gap: 6px; min-width: 0; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.spark-item:hover { background: #161b22; }
.spark-svg { flex: 1 1 auto; min-width: 0; width: 100%; height: 18px; }
.spark-label { font-size: 10px; color: #f6c542; font-family: 'JetBrains Mono', monospace; min-width: 28px; flex: 0 0 auto; }
.spark-label.gvz { color: #fbc02d; }
.spark-label.kt  { color: #f6c542; }
.spark-label.xau { color: #f6c542; }
.spark-label.y10 { color: #26c6da; }
.spark-label.gc  { color: #f6c542; }
.spark-value { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #888; min-width: 48px; flex: 0 0 auto; text-align: right; }
.spark-value.positive { color: #4caf50; }
.spark-value.negative { color: #f44336; }
.spark-value.amber    { color: #fbc02d; }
/* Gold K_t live overlay bridge (2026-09-02) — marks the K_t value as the
   combined (base + live overlay) figure rather than the frozen base return. */
.overlay-marker { color: #f6c542; font-size: 10px; font-weight: 700; flex: 0 0 auto; cursor: default; }

@media (max-width: 768px) {
  .sparkline-bar { grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 4px 8px; }
  .spark-label { font-size: 9px; }
  .spark-value { font-size: 9px; }
}
</style>
